import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const SERVICE_HANDLES = new Set(['montage-service', 'anfahrtskosten', 'montage-hoehenzuschlag']);

/**
 * Muss synchron boolean zurückgeben – async würde product-form blockieren.
 * @param {HTMLElement} formComponent
 * @param {Event} event
 * @returns {boolean}
 */
function handleMontageSubmit(formComponent, event) {
  const sectionId = formComponent.dataset.sectionId;
  const montageBlock = document.querySelector(`montage-service-block[data-section-id="${sectionId}"]`);
  const montageCheckbox = montageBlock?.querySelector('input[name="bw_montage_enabled"]');
  if (!(montageCheckbox instanceof HTMLInputElement) || !montageCheckbox.checked) {
    return false;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  void processMontageSubmit(formComponent);
  return true;
}

/**
 * @param {HTMLElement} formComponent
 */
async function processMontageSubmit(formComponent) {
  const sectionId = formComponent.dataset.sectionId;
  const montageBlock = document.querySelector(`montage-service-block[data-section-id="${sectionId}"]`);

  const form = formComponent.querySelector('form');
  if (!form || !form.checkValidity()) return;

  const config = await loadConfig();
  const variantInput = form.querySelector('input[name="id"]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const mainVariantId = Number(variantInput instanceof HTMLInputElement ? variantInput.value : 0);
  const quantity = Number(quantityInput instanceof HTMLInputElement ? quantityInput.value : 1);
  const montageVariantId = Number(montageBlock?.dataset.montageVariantId);
  const hoehenVariantId = Number(montageBlock?.dataset.hoehenVariantId || config.hoehen_variant_id);
  const hoehenCheckbox = montageBlock?.querySelector('input[name="bw_montage_hoehe"]');
  const bundleId = createBundleId();
  const productTitle = montageBlock?.dataset.productTitle || 'Produkt';

  if (!mainVariantId || !montageVariantId) return;

  /** @type {Array<{id: number, quantity: number, parent_id?: number, properties?: Record<string, string>}>} */
  const items = [
    {
      id: mainVariantId,
      quantity,
      properties: { _bw_bundle: bundleId, _bw_montage: '1' },
    },
    {
      id: montageVariantId,
      quantity,
      parent_id: mainVariantId,
      properties: {
        _bw_bundle: bundleId,
        _bw_service: 'montage',
      },
    },
  ];

  if (hoehenCheckbox instanceof HTMLInputElement && hoehenCheckbox.checked && hoehenVariantId) {
    items.push({
      id: hoehenVariantId,
      quantity,
      parent_id: mainVariantId,
      properties: {
        _bw_bundle: bundleId,
        _bw_service: 'hoehe',
      },
    });
  }

  try {
    await clearMontageTravelState();
    const response = await addCartItems(items);
    if (response.status) throw new Error(response.message || response.description);
    await refreshMontageCheckoutState();
    formComponent.dispatchEvent(
      new CartAddEvent(response, String(mainVariantId), {
        source: 'montage-service',
        itemCount: response.item_count ?? quantity,
        productId: formComponent.dataset.productId,
        sections: response.sections,
      })
    );
  } catch (error) {
    console.error(error);
    const errorEl = formComponent.querySelector('[ref="addToCartTextError"]');
    if (errorEl) {
      errorEl.classList.remove('hidden');
      errorEl.appendChild(
        document.createTextNode(error.message || 'Montage konnte nicht hinzugefügt werden.')
      );
    }
  }
}

window.BWMontageService = { handleSubmit: handleMontageSubmit };

/**
 * @returns {Promise<Record<string, unknown>>}
 */
async function loadConfig() {
  if (window.BWMontageConfig) return window.BWMontageConfig;
  const el = document.getElementById('bw-montage-config');
  if (el?.textContent) {
    window.BWMontageConfig = JSON.parse(el.textContent);
    return window.BWMontageConfig;
  }
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}assets/montage-service-config.json`);
  window.BWMontageConfig = await response.json();
  return window.BWMontageConfig;
}

/**
 * @returns {string}
 */
function createBundleId() {
  return `bw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * @returns {Promise<Record<string, unknown>>}
 */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`);
  return response.json();
}

/**
 * @param {number|string} line
 * @param {number} quantity
 */
async function changeCartLine(line, quantity) {
  const response = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line, quantity }),
  });
  return response.json();
}

/**
 * @param {Array<Record<string, unknown>>} items
 * @param {string} handle
 * @returns {number | null}
 */
function findLineIndexByHandle(items, handle) {
  const index = items.findIndex((item) => item.handle === handle);
  return index === -1 ? null : index + 1;
}

/**
 * @param {{ attributes?: Record<string, string>, note?: string }} payload
 */
async function updateCart(payload) {
  const response = await fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  return response.json();
}

/**
 * @param {Array<{id: number, quantity: number, properties?: Record<string, string>}>} items
 */
async function addCartItems(items) {
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sections = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sections.push(item.dataset.sectionId);
    }
  });

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sections.join(',') }),
  });
  return response.json();
}

/** @type {Promise<void> | null} */
let googleMapsLoaderPromise = null;

const GOOGLE_MAPS_SETUP_HINT =
  'Google Maps API ist nicht aktiviert. In der Google Cloud Console für den API-Key aktivieren: „Maps JavaScript API“ und „Routes API“ (Billing erforderlich).';

const GOOGLE_MAPS_REFERRER_HINT =
  'Google Maps API-Key ist für diese Shop-Domain nicht freigegeben. In der Google Cloud Console unter „Anwendungseinschränkungen“ → HTTP-Referrer eintragen: https://beleuchtung-welt.myshopify.com/* und https://*.myshopify.com/*';

const GOOGLE_MAPS_API_RESTRICTION_HINT =
  'Der API-Key darf die benötigten Google-Dienste nicht aufrufen. Unter „API-Einschränkungen“ aktivieren: Maps JavaScript API und Routes API. Distance Matrix API / Directions API werden nicht mehr benötigt.';

/**
 * @param {unknown} error
 * @returns {string}
 */
function formatMapsError(error) {
  const message = error instanceof Error ? error.message : String(error);
  if (message.includes('RefererNotAllowedMapError') || message.includes('RefererNotAllowed')) {
    return GOOGLE_MAPS_REFERRER_HINT;
  }
  if (message.includes('ApiTargetBlockedMapError') || message.includes('ApiTargetBlocked')) {
    return GOOGLE_MAPS_API_RESTRICTION_HINT;
  }
  if (
    message.includes('ApiNotActivatedMapError') ||
    message.includes('ApiNotActivated') ||
    message.includes('REQUEST_DENIED')
  ) {
    return GOOGLE_MAPS_SETUP_HINT;
  }
  if (message.includes('Google Maps API Key fehlt')) {
    return message;
  }
  return message || 'Berechnung fehlgeschlagen.';
}

/**
 * @param {string} apiKey
 * @returns {Promise<void>}
 */
function loadGoogleMaps(apiKey) {
  if (!apiKey) {
    return Promise.reject(
      new Error('Google Maps API Key fehlt. Bitte unter Theme-Einstellungen → Montage-Service eintragen.')
    );
  }

  if (window.google?.maps?.importLibrary) {
    return Promise.resolve();
  }

  if (googleMapsLoaderPromise) {
    return googleMapsLoaderPromise;
  }

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const id = 'bw-google-maps-script';
    const waitForImportLibrary = () => {
      if (window.google?.maps?.importLibrary) {
        resolve();
        return;
      }
      window.setTimeout(waitForImportLibrary, 50);
    };

    if (document.getElementById(id)) {
      waitForImportLibrary();
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async`;
    script.onerror = () => {
      googleMapsLoaderPromise = null;
      reject(new Error('Google Maps konnte nicht geladen werden.'));
    };
    script.onload = waitForImportLibrary;
    document.head.appendChild(script);
  });

  return googleMapsLoaderPromise;
}

/**
 * @param {string} origin
 * @param {string} destination
 * @returns {Promise<number>}
 */
async function getDrivingDistanceViaRouteMatrix(origin, destination) {
  const { RouteMatrix } = await google.maps.importLibrary('routes');
  const response = await RouteMatrix.computeRouteMatrix({
    origins: [origin],
    destinations: [destination],
    travelMode: 'DRIVING',
    fields: ['distanceMeters', 'condition'],
  });

  const matrix = response?.matrix ?? response;
  const item = matrix?.rows?.[0]?.items?.[0];
  if (!item || item.condition !== 'ROUTE_EXISTS' || typeof item.distanceMeters !== 'number') {
    throw new Error('Adresse konnte nicht gefunden werden.');
  }

  return item.distanceMeters / 1000;
}

/**
 * @param {string} origin
 * @param {string} destination
 * @returns {Promise<number>}
 */
async function getDrivingDistanceKm(origin, destination) {
  const apiKey = window.BWMontageRuntime?.googleMapsApiKey || '';
  await loadGoogleMaps(apiKey);

  try {
    return await getDrivingDistanceViaRouteMatrix(origin, destination);
  } catch (error) {
    if (error instanceof Error && error.message === 'Adresse konnte nicht gefunden werden.') {
      throw error;
    }
    throw new Error(formatMapsError(error));
  }
}

/**
 * @param {Array<Record<string, unknown>>} items
 * @param {string} handle
 */
function findLineByHandle(items, handle) {
  return items.find((item) => item.handle === handle);
}

/**
 * @param {Array<Record<string, unknown>>} items
 * @param {string} bundleId
 */
function findMontageLineForBundle(items, bundleId) {
  return items.find(
    (item) =>
      item.handle === 'montage-service' &&
      item.properties &&
      Object.entries(item.properties).some(([k, v]) => k === '_bw_bundle' && v === bundleId)
  );
}

let montageCalculateInProgress = false;

/**
 * @returns {Promise<void>}
 */
async function refreshCartSections() {
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.add(component.dataset.sectionId);
    }
  });

  await Promise.all(
    [...sectionIds].map((sectionId) => sectionRenderer.renderSection(sectionId, { cache: false }))
  );
}

/**
 * @param {HTMLFormElement} form
 */
async function handleMontageCartFormSubmit(form) {
  if (montageCalculateInProgress) return;

  const panel = form.closest('#bw-montage-cart-panel') || document.getElementById('bw-montage-cart-panel');
  const errorEl = panel?.querySelector('[data-bw-montage-error]');
  const submitButton = form.querySelector('[data-bw-montage-calculate]');

  if (errorEl) errorEl.textContent = '';

  const plzInput = form.querySelector('input[name="montage_plz"]');
  const cityInput = form.querySelector('input[name="montage_ort"]');
  const streetInput = form.querySelector('input[name="montage_strasse"]');

  const plz = plzInput instanceof HTMLInputElement ? plzInput.value.trim() : '';
  const city = cityInput instanceof HTMLInputElement ? cityInput.value.trim() : '';
  const street = streetInput instanceof HTMLInputElement ? streetInput.value.trim() : '';

  if (!/^\d{5}$/.test(plz)) {
    if (errorEl) errorEl.textContent = 'Bitte eine gültige 5-stellige PLZ eingeben.';
    return;
  }

  const config = await loadConfig();
  const destination = [street, `${plz} ${city || config.base_city}`, 'Deutschland'].filter(Boolean).join(', ');
  const origin = config.base_address;

  montageCalculateInProgress = true;
  if (submitButton instanceof HTMLButtonElement) {
    submitButton.disabled = true;
    submitButton.dataset.bwLoading = 'true';
  }

  try {
    const distanceKm = await getDrivingDistanceKm(origin, destination);
    const roundedKm = Math.ceil(distanceKm);
    const freeKm = Number(config.free_km || 1);
    const maxKm = Number(config.max_km || 150);
    const billableKm = Math.max(0, roundedKm - freeKm);

    if (roundedKm > maxKm) {
      if (errorEl) {
        errorEl.textContent = `Montage ist nur bis ${maxKm} km Entfernung möglich (berechnet: ${roundedKm} km).`;
      }
      return;
    }

    const cart = await fetchCart();
    const anfahrtVariantId = Number(config.anfahrt_variant_id || panel?.dataset.anfahrtVariantId);
    if (!anfahrtVariantId) throw new Error('Anfahrt-Variante ist nicht konfiguriert.');

    const anfahrtLine = findLineIndexByHandle(cart.items, 'anfahrtskosten');
    if (anfahrtLine) {
      await changeCartLine(anfahrtLine, 0);
    }

    if (billableKm > 0) {
      const addResponse = await addCartItems([
        {
          id: anfahrtVariantId,
          quantity: billableKm,
          properties: {
            _bw_service: 'anfahrt',
            Gesamtentfernung: `${roundedKm} km`,
            'Berechnete km': `${billableKm} km`,
            'Montage-PLZ': plz,
          },
        },
      ]);
      if (addResponse.status) throw new Error(addResponse.message || addResponse.description);
    }

    const montageNote = [
      `Montage-PLZ: ${plz}${city ? ` ${city}` : ''}`,
      street ? `Adresse: ${street}` : null,
      `Gesamtentfernung: ${roundedKm} km`,
      billableKm > 0
        ? `Berechnete km: ${billableKm} km (${freeKm} km kostenfrei)`
        : `Anfahrt kostenfrei (bis ${freeKm} km)`,
    ]
      .filter(Boolean)
      .join('\n');

    await updateCart({
      attributes: {
        montage_plz: plz,
        montage_ort: city,
        montage_strasse: street,
        montage_km: String(roundedKm),
        montage_anfahrt_km: String(billableKm),
        montage_berechnet: '1',
      },
      note: montageNote,
    });

    const cartAfter = await fetchCart();
    await refreshMontageCheckoutState();

    document.dispatchEvent(
      new CartUpdateEvent(cartAfter, 'montage-service', {
        source: 'montage-service',
        itemCount: cartAfter.item_count,
      })
    );

    await refreshCartSections();
    await refreshMontageCheckoutState();
  } catch (error) {
    const message = formatMapsError(error);
    const activePanel = document.getElementById('bw-montage-cart-panel');
    const activeError = activePanel?.querySelector('[data-bw-montage-error]');
    if (activeError instanceof HTMLElement) {
      activeError.textContent = message;
    }
  } finally {
    montageCalculateInProgress = false;
    const activeButton = document
      .getElementById('bw-montage-cart-panel')
      ?.querySelector('[data-bw-montage-calculate]');
    if (activeButton instanceof HTMLButtonElement) {
      activeButton.disabled = false;
      delete activeButton.dataset.bwLoading;
    }
  }
}

/**
 * @param {HTMLElement} panel
 * @param {boolean} calculated
 */
function setMontagePanelView(panel, calculated) {
  const formWrap = panel.querySelector('[data-bw-montage-form-wrap]');
  const summary = panel.querySelector('[data-bw-montage-summary]');
  panel.classList.toggle('bw-montage-cart-panel--calculated', calculated);
  panel.classList.toggle('bw-montage-cart-panel--required', !calculated);
  if (formWrap instanceof HTMLElement) {
    if (calculated) formWrap.setAttribute('hidden', '');
    else formWrap.removeAttribute('hidden');
  }
  if (summary instanceof HTMLElement) {
    if (calculated) summary.removeAttribute('hidden');
    else summary.setAttribute('hidden', '');
  }
}

function initCartPanel() {
  if (window.BWMontagePanelInit) return;
  window.BWMontagePanelInit = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const recalculate = target.closest('[data-bw-montage-recalculate]');
      if (!(recalculate instanceof HTMLButtonElement)) return;

      const panel = recalculate.closest('#bw-montage-cart-panel');
      if (!(panel instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopPropagation();
      setMontagePanelView(panel, false);
      const errorEl = panel.querySelector('[data-bw-montage-error]');
      if (errorEl instanceof HTMLElement) errorEl.textContent = '';
      panel.querySelector('input[name="montage_plz"]')?.focus();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-bw-montage-form]')) return;

      event.preventDefault();
      event.stopPropagation();
      void handleMontageCartFormSubmit(form);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('[data-bw-montage-calculate]');
      if (!(button instanceof HTMLButtonElement)) return;

      const form = button.closest('[data-bw-montage-form]');
      if (!(form instanceof HTMLFormElement)) return;

      event.preventDefault();
      event.stopPropagation();
      void handleMontageCartFormSubmit(form);
    },
    true
  );
}

async function syncMontageQuantities() {
  const cart = await fetchCart();
  /** @type {Array<{line: number, quantity: number}>} */
  const updates = [];

  for (let index = 0; index < cart.items.length; index += 1) {
    const lineItem = cart.items[index];
    if (!lineItem || SERVICE_HANDLES.has(lineItem.handle)) continue;

    const bundleId = lineItem.properties?._bw_bundle;
    if (!bundleId) continue;

    const mainLine = index + 1;
    const newQty = lineItem.quantity;

    for (let serviceIndex = 0; serviceIndex < cart.items.length; serviceIndex += 1) {
      const serviceItem = cart.items[serviceIndex];
      if (serviceItem.properties?._bw_bundle !== bundleId) continue;
      if (serviceItem.handle !== 'montage-service' && serviceItem.handle !== 'montage-hoehenzuschlag') continue;
      if (serviceItem.quantity === newQty) continue;
      updates.push({ line: serviceIndex + 1, quantity: newQty });
    }

    if (newQty === 0) {
      for (let serviceIndex = cart.items.length - 1; serviceIndex >= 0; serviceIndex -= 1) {
        const serviceItem = cart.items[serviceIndex];
        if (serviceItem.properties?._bw_bundle === bundleId && SERVICE_HANDLES.has(serviceItem.handle)) {
          updates.push({ line: serviceIndex + 1, quantity: 0 });
        }
      }
    }

    void mainLine;
  }

  if (!updates.length) return;
  for (const update of updates) {
    await changeCartLine(update.line, update.quantity);
  }
}

/**
 * @param {Record<string, unknown>} cart
 */
function getMontageCheckoutState(cart) {
  const hasMontage = Boolean(cart?.items?.some((item) => item.handle === 'montage-service'));
  const calculated =
    cart?.attributes?.montage_berechnet === '1' && Boolean(cart?.attributes?.montage_km);
  return {
    hasMontage,
    calculated,
    blocked: hasMontage && !calculated,
    itemCount: Number(cart?.item_count || 0),
  };
}

/**
 * @returns {Promise<{hasMontage: boolean, calculated: boolean, blocked: boolean}>}
 */
async function refreshMontageCheckoutState() {
  const cart = await fetchCart();
  const state = getMontageCheckoutState(cart);
  window.BWMontageCartState = state;
  applyMontageCheckoutUi(state);
  return state;
}

/**
 * @param {{ blocked: boolean }} state
 */
function applyMontageCheckoutUi(state) {
  document.querySelectorAll('#checkout, button[name="checkout"]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (state.blocked) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.bwCheckoutBlocked = 'true';
    } else if (state.itemCount > 0) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      delete button.dataset.bwCheckoutBlocked;
    }
  });

  document
    .querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart')
    .forEach((element) => {
      if (state.blocked) {
        element.setAttribute('hidden', '');
      } else {
        element.removeAttribute('hidden');
      }
    });

  const panel = document.getElementById('bw-montage-cart-panel');
  if (panel) {
    panel.classList.toggle('bw-montage-cart-panel--required', state.blocked);
    panel.dataset.bwMontageBlocked = state.blocked ? 'true' : 'false';
  }
}

function showMontageCheckoutError() {
  const panel = document.getElementById('bw-montage-cart-panel');
  const errorEl = panel?.querySelector('[data-bw-montage-error]');
  const alertEl = document.querySelector('[data-bw-montage-checkout-alert]');
  const message =
    'Bitte zuerst die Montage-PLZ eingeben und auf „Entfernung berechnen“ klicken. Erst danach ist der Checkout möglich.';

  if (errorEl instanceof HTMLElement) {
    errorEl.textContent = message;
  }
  if (alertEl instanceof HTMLElement) {
    alertEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  panel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  panel?.querySelector('input[name="montage_plz"]')?.focus();
}

async function clearMontageTravelState() {
  const cart = await fetchCart();
  const anfahrtLine = findLineIndexByHandle(cart.items, 'anfahrtskosten');
  if (anfahrtLine) {
    await changeCartLine(anfahrtLine, 0);
  }

  await updateCart({
    attributes: {
      montage_plz: '',
      montage_ort: '',
      montage_strasse: '',
      montage_km: '',
      montage_anfahrt_km: '',
      montage_berechnet: '',
    },
    note: '',
  });
}

/**
 * Block checkout until montage travel costs are calculated in cart.
 */
function initCheckoutGuard() {
  void refreshMontageCheckoutState();

  document.addEventListener(ThemeEvents.cartUpdate, () => {
    void refreshMontageCheckoutState();
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!window.BWMontageCartState?.blocked) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const checkoutTrigger = target.closest(
        '#checkout, button[name="checkout"], shopify-accelerated-checkout-cart, .additional-checkout-buttons, [data-shopify="payment-button"]'
      );
      if (!checkoutTrigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      showMontageCheckoutError();
    },
    true
  );

  const cartForm = document.getElementById('cart-form');
  cartForm?.addEventListener(
    'submit',
    (event) => {
      if (!window.BWMontageCartState?.blocked) return;

      const submitter = event.submitter;
      if (!(submitter instanceof HTMLButtonElement) || submitter.name !== 'checkout') return;

      event.preventDefault();
      event.stopPropagation();
      showMontageCheckoutError();
    },
    true
  );
}

/**
 * Hide Shop Pay / dynamic checkout when montage is selected (bypasses batch add).
 */
function initMontagePdpUi() {
  document.querySelectorAll('montage-service-block').forEach((block) => {
    const montageCheckbox = block.querySelector('input[name="bw_montage_enabled"]');
    const hoehenCheckbox = block.querySelector('input[name="bw_montage_hoehe"]');
    if (!(montageCheckbox instanceof HTMLInputElement)) return;

    const hoehenControl = hoehenCheckbox?.closest('.checkbox');

    const syncHoehenState = () => {
      if (!(hoehenCheckbox instanceof HTMLInputElement)) return;
      const montageOn = montageCheckbox.checked;
      hoehenCheckbox.disabled = !montageOn;
      hoehenControl?.classList.toggle('checkbox--disabled', !montageOn);
      if (!montageOn) hoehenCheckbox.checked = false;
    };

    const toggleAcceleratedCheckout = () => {
      const hide = montageCheckbox.checked;
      document
        .querySelectorAll('.accelerated-checkout-block, .additional-checkout-buttons, shopify-accelerated-checkout')
        .forEach((element) => {
          if (hide) {
            element.setAttribute('hidden', '');
          } else {
            element.removeAttribute('hidden');
          }
        });
    };

    const onMontageChange = () => {
      syncHoehenState();
      toggleAcceleratedCheckout();
    };

    montageCheckbox.addEventListener('change', onMontageChange);

    const hintEl = block.querySelector('[data-bw-hoehe-hint]');
    if (hintEl instanceof HTMLElement && hoehenCheckbox instanceof HTMLInputElement) {
      hintEl.addEventListener('click', () => {
        if (!montageCheckbox.checked) {
          montageCheckbox.checked = true;
          onMontageChange();
        }
        if (!hoehenCheckbox.disabled) {
          hoehenCheckbox.checked = !hoehenCheckbox.checked;
        }
      });
    }

    syncHoehenState();
    toggleAcceleratedCheckout();
  });
}

function initMontageCartFeatures() {
  if (window.BWMontageCartInit) return;
  window.BWMontageCartInit = true;

  window.BWMontageRuntime = {
    googleMapsApiKey: document.body.dataset.bwGoogleMapsKey || '',
  };
  initCartPanel();
  initCheckoutGuard();
  initMontagePdpUi();
}

document.addEventListener('DOMContentLoaded', initMontageCartFeatures);

let montageSyncInProgress = false;

document.addEventListener(ThemeEvents.cartUpdate, () => {
  if (montageSyncInProgress) return;
  montageSyncInProgress = true;
  syncMontageQuantities()
    .catch(console.error)
    .finally(() => {
      montageSyncInProgress = false;
    });
});
